/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect, useRef} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {Flex, Skeleton, Stack, HStack, Text, Button, useMultiStyleConfig} from '@chakra-ui/react'
import {useProducts, useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import ChakraLink from '@salesforce/retail-react-app/app/components/link'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useToast} from '../../../../app/hooks/use-toast'
import {useWishList} from '../../../hooks/use-wish-list'
import PageActionPlaceHolder from '../../../components/page-action-placeholder'
import WishlistProductItem from './partials/wishlist-product-item'
import WishlistPrimaryAction from './partials/wishlist-primary-action'
import WishlistSecondaryButtonGroup from './partials/wishlist-secondary-button-group'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import UnavailableProductConfirmationModal from '@salesforce/retail-react-app/app/components/unavailable-product-confirmation-modal'
import Feedback from '../../../components/feedback'
import ShareWishlistModal from './partials/share-wishlist-modal'
import ModalLogin from '../../../components/login/modal-login'
import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../../custom-api/layers/default'
import {getPriceData} from '../../../utils/product-utils'
import {useBooleanCustomPreference} from '../../../hooks/use-custom-preference'
import GenericHtmlPage from '../../generic-html-page'
// import {useLocation} from 'react-router'
// import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
// import {Helmet} from 'react-helmet'

const numberOfSkeletonItems = 3
const AccountWishlist = () => {
    const hideAccountPref = useBooleanCustomPreference('hideAccount')
    const hideAccount = hideAccountPref !== null ? hideAccountPref : false
    const navigate = useNavigation()
    const {formatMessage} = useIntl()
    const toast = useToast()
    const {addLayer} = useGoogleTagManager()
    const {data: customer, isFetching: isCustomerFetching} = useCurrentCustomer()
    const styleVariant = customer.isGuest ? 'guest' : ''
    const styles = useMultiStyleConfig('Wishlist', {variant: styleVariant})
    // const location = useLocation()
    // const {customParams} = getConfig()
    // const currentPath = 'https://' + customParams.currentHost + location.pathname
    // const webPageSchemaMarkup = {
    //     '@context': 'https://schema.org/',
    //     '@type': 'WebPage',
    //     name: 'Account wishlist',
    //     description: 'View and manage your wishlist items',
    //     url: currentPath,
    // }

    const headingRef = useRef()
    useEffect(() => {
        // Focus the 'Wishlist' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    const [selectedItem, setSelectedItem] = useState(undefined)
    const [isWishlistItemLoading] = useState(false)

    const {data: wishListData, isLoading: isWishListLoading} = useWishList()
    const productIds = wishListData?.customerProductListItems?.map((item) => item.productId)

    const {data: productsData, isLoading: isProductsLoading} = useProducts(
        {
            parameters: {
                ids: productIds?.join(','),
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'wishlist-index',
            },
        },
        {enabled: productIds?.length > 0},
    )

    // If a product is added to the wishlist many times, it gets collapased into 1 line item
    const wishListItems = wishListData?.customerProductListItems?.reduce((itemsData, item) => {
        const productData = productsData?.data?.find((product) => product.id === item.productId)
        return {...itemsData, [item.productId]: {...item, product: productData}}
    }, {})

    const updateCustomerProductListItem = useShopperCustomersMutation(
        'updateCustomerProductListItem',
    )
    const deleteCustomerProductListItem = useShopperCustomersMutation(
        'deleteCustomerProductListItem',
    )

    const handleUnavailableProducts = async (unavailableProductIds) => {
        if (!unavailableProductIds.length) return
        await Promise.all(
            unavailableProductIds.map(async (id) => {
                const item = wishListItems?.find((item) => {
                    return item.productId.toString() === id.toString()
                })
                const parameters = {
                    customerId: customer.customerId,
                    itemId: item?.id,
                    listId: wishListData?.id,
                }
                await deleteCustomerProductListItem.mutateAsync({parameters})
            }),
        )
    }

    const handleItemQuantityChanged = async (quantity, item) => {
        let isValidChange = false
        setSelectedItem(item.productId)

        const body = {
            ...item,
            quantity: parseInt(quantity),
            public: true,
        }
        // To meet expected schema, remove the custom `product` we added
        delete body.product

        const parameters = {
            customerId: customer.customerId,
            itemId: item.id,
            listId: wishListData?.id,
        }

        const mutation =
            parseInt(quantity) > 0
                ? updateCustomerProductListItem.mutateAsync({body, parameters})
                : deleteCustomerProductListItem.mutateAsync({parameters})

        try {
            await mutation
            isValidChange = true
            setSelectedItem(undefined)
        } catch (err) {
            toast({
                title: formatMessage(API_ERROR_MESSAGE),
                status: 'error',
            })
        }

        // If true, the quantity picker would immediately update its number
        // without waiting for the invalidated lists data to finish refetching
        return isValidChange
    }

    const hasWishlistItems = Object.keys(wishListItems ?? {}).length > 0
    const isPageLoading =
        isCustomerFetching || hasWishlistItems ? isProductsLoading : isWishListLoading

    const [removedProductName, setRemovedProductName] = useState('')

    const handleItemRemoved = (productName) => {
        setRemovedProductName(productName)
        setTimeout(() => setRemovedProductName(''), 3000) // Clear after 3s
    }

    useEffect(() => {
        if (hasWishlistItems && !isProductsLoading) {
            addLayer(polish_ecommerce_gtm_event.bind(this))
            addLayer(
                item_gtm_event.bind(this, {
                    event: 'view_item_list',
                    item_list_name: 'wishlist',
                    items: Object.keys(wishListItems).map((key, i) => {
                        const {product, productId} = wishListItems[key]
                        //TODO: on this level we dont receive any tax data or rate, how to proceed?
                        return {
                            item_name: product?.c_defaultName,
                            item_id: productId,
                            item_category: product?.c_defaultCategoryName,
                            item_variant: productId,
                            price: product?.price, //? This field should be net price not gross
                            fullprice: getPriceData(product)?.isOnSale ? 'reduced' : 'fullprice',
                            rating_points: '', //?Bazaarvoice rating
                            erp_category: product?.c_materialGroup,
                            prdha_category: product?.c_prdha_category,
                            index: i + 1,
                            stock_status:
                                product?.inventory?.stockLevel > 0 ? 'in-stock' : 'out-of-stock',
                        }
                    }),
                }),
            )
        }
    }, [hasWishlistItems, isProductsLoading])

    return !hideAccount ? (
        <Stack {...styles?.wishlistStack} data-testid="account-wishlist-page">
            {/* <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
            </Helmet> */}
            <Stack {...styles?.wishlistLogin}>
                {customer.isGuest && (
                    <Button
                        {...styles?.wishlistBackButton}
                        variant="text-button"
                        as={ChakraLink}
                        to="/"
                    >
                        <FormattedMessage
                            defaultMessage="Back to homepage"
                            id="login_form.message.back_to_homepage"
                        />
                    </Button>
                )}
                <Flex {...(customer.isGuest === false && styles?.wishlistShare)}>
                    <Text {...styles?.wishlistTitle} ref={headingRef}>
                        <FormattedMessage
                            defaultMessage="Wishlist"
                            id="account_wishlist.title.wishlist"
                        />
                    </Text>

                    {/* ShareWishlist hidden since it doesn't work correctly */}
                    {!customer.isGuest && (
                        <ShareWishlistModal wishListData={wishListData} customer={customer} />
                    )}
                </Flex>
                {customer.isGuest && (
                    <Stack {...styles?.wishlistLoginStack}>
                        <Text {...styles?.wishlistTextSuggestion}>
                            <FormattedMessage
                                defaultMessage="Log in or create a new account to save your Wishlist and be able to view it at any time, from all your devices"
                                id="account_wishlist.description.login_or_create_account"
                            />
                        </Text>
                        <Flex {...styles?.wishlistLoginButtonGroup}>
                            <ModalLogin />
                            <Button
                                variant="secondary"
                                {...styles?.wishlistLoginButton}
                                as={ChakraLink}
                                to="/registration"
                            >
                                <FormattedMessage
                                    defaultMessage="Registrate"
                                    id="login_form.message.registrate"
                                />
                            </Button>
                        </Flex>
                    </Stack>
                )}
            </Stack>

            {removedProductName && (
                <HStack {...styles?.feedbackContainer}>
                    <Feedback
                        message={
                            <FormattedMessage
                                id="account_wishlist.feedback.removed"
                                defaultMessage="{productName} has been removed from your wishlist."
                                values={{productName: removedProductName}}
                            />
                        }
                        variant="removeFeedbackWishlist"
                        status="success"
                        enableIcon={false}
                    />
                </HStack>
            )}

            {isPageLoading && (
                <Flex {...styles?.gridProduct} data-testid="sf-wishlist-skeleton">
                    {new Array(numberOfSkeletonItems).fill(0).map((i, idx) => (
                        <Stack
                            key={idx}
                            p={[4, 6]}
                            my={4}
                            border="1px solid"
                            borderColor="gray.100"
                            borderRadius="base"
                        >
                            <Skeleton boxSize={['88px', 36]} mr={4} />

                            <Stack spacing={2}>
                                <Skeleton h="20px" w="112px" />
                                <Skeleton h="20px" w="84px" />
                                <Skeleton h="20px" w="140px" />
                            </Stack>
                        </Stack>
                    ))}
                </Flex>
            )}

            {!isPageLoading && !hasWishlistItems && (
                <PageActionPlaceHolder
                    data-testid="empty-wishlist"
                    heading={formatMessage({
                        defaultMessage: 'Your wishlist is empty',
                        id: 'account_wishlist.heading.empty_wishlist',
                    })}
                    text={formatMessage({
                        defaultMessage: 'You have not saved any products to your wishlist yet',
                        id: 'account_wishlist.description.continue_shopping',
                    })}
                    buttonText={formatMessage({
                        defaultMessage: 'Start shopping',
                        id: 'account_wishlist.button.start_shopping',
                    })}
                    buttonProps={{leftIcon: undefined}}
                    onButtonClick={() => navigate('/')}
                    variant="empty-wishlist"
                />
            )}

            <Flex {...styles?.gridProduct}>
                {!isPageLoading &&
                    wishListItems &&
                    Object.keys(wishListItems).map((key) => {
                        const item = wishListItems[key]

                        return (
                            <WishlistProductItem
                                key={item.id}
                                product={{
                                    ...item.product,
                                    quantity: item.quantity,
                                }}
                                showLoading={
                                    (updateCustomerProductListItem.isLoading ||
                                        deleteCustomerProductListItem.isLoading ||
                                        isWishlistItemLoading) &&
                                    selectedItem === item.productId
                                }
                                primaryAction={<WishlistPrimaryAction />}
                                onItemQuantityChange={(quantity) =>
                                    handleItemQuantityChanged(quantity, item)
                                }
                                secondaryActions={
                                    <WishlistSecondaryButtonGroup
                                        productListItemId={item?.id}
                                        productName={
                                            item?.product?.c_variantIdentifier ||
                                            item?.product?.name ||
                                            item?.product?.productName
                                        }
                                        // Focus to 'Wishlist' header after remove for accessibility
                                        focusElementOnRemove={headingRef}
                                        onItemRemoved={handleItemRemoved}
                                    />
                                }
                            />
                        )
                    })}
            </Flex>
            <UnavailableProductConfirmationModal
                productItems={wishListData?.customerProductListItems}
                handleUnavailableProducts={handleUnavailableProducts}
            />
        </Stack>
    ) : (
        <GenericHtmlPage />
    )
}

export default AccountWishlist
